/**
 * Mulberry32 step. The whole PRNG state is a single uint32, which lets it live
 * inside the serializable game state so simulations are reproducible (handy for
 * tests, saves and keeping multiplayer hosts deterministic).
 */
export function nextRandom(seed: number): [value: number, nextSeed: number] {
  const next = (seed + 0x6d2b79f5) | 0;
  let t = next;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return [((t ^ (t >>> 14)) >>> 0) / 4294967296, next >>> 0];
}

/** Convenience wrapper that reads and advances `holder.rngState` in place. */
export class Rng {
  constructor(private readonly holder: { rngState: number }) {}

  next(): number {
    const [value, seed] = nextRandom(this.holder.rngState);
    this.holder.rngState = seed;
    return value;
  }

  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  chance(probability: number): boolean {
    return this.next() < probability;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick called with an empty list');
    return items[Math.floor(this.next() * items.length)] as T;
  }

  /** Picks `count` distinct items (or all of them if there are fewer). */
  sample<T>(items: readonly T[], count: number): T[] {
    const pool = [...items];
    const out: T[] = [];
    while (out.length < count && pool.length > 0) {
      const index = Math.floor(this.next() * pool.length);
      out.push(pool.splice(index, 1)[0] as T);
    }
    return out;
  }
}

/** Stateless seeded generator for cosmetic randomness (scenery placement etc). */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    const [value, next] = nextRandom(state);
    state = next;
    return value;
  };
}
